import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Typography } from '@mui/material';
import ModalDialog from '../UI/Dialog/ModalDialog';
import Button from '../UI/Buttons/Button';
import setAuthToken from '../../setAuthToken';
import { setCurrentUser } from '../../actions/authentication';

const noop = () => {};

const SignatureGate = () => {
    const auth = useSelector(s => s.auth);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const sigPad = useRef(null);
    const [sigEmpty, setSigEmpty] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const shouldShow = auth?.isAuthenticated === true
        && auth?.user != null
        && auth.user.firstLogin !== true
        && auth.user.hasValidSignature === false
        && auth.user.kiosk !== true
        && auth.isInternalAdmin !== true
        && !dismissed;

    if (!shouldShow) return null;

    const clearSig = () => {
        sigPad.current?.clear();
        setSigEmpty(true);
    };

    const acceptSig = async () => {
        if (sigPad.current == null || sigPad.current.isEmpty()) {
            dispatch({ type: 'CREATE_ALERT', payload: { message: t('signatureEmpty'), title: t('error'), severity: 'error' } });
            return;
        }
        const signatureImage = sigPad.current.getTrimmedCanvas().toDataURL('image/png');
        setSaving(true);
        try {
            const res = await axios.post('/api/user/savesignature', { signatureImage });
            const token = res.data?.token;
            if (token != null) {
                sessionStorage.jwtToken = token;
                setAuthToken(token);
                dispatch(setCurrentUser(jwtDecode(token), null));
            }
            dispatch({ type: 'CREATE_ALERT', payload: { message: t('signatureSaved'), title: t('success'), severity: 'success' } });
            setDismissed(true);
        } catch (err) {
            const message = err.response?.data?.message || err.response?.data || err.message || t('error');
            dispatch({ type: 'CREATE_ALERT', payload: { message, title: t('error'), severity: 'error' } });
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalDialog
            dialogStatus={true}
            toggleDialog={noop}
            disableClickAway
            title={t('signatureRequired')}
            maxWidth="md"
            fullWidth
            buttonStack={(
                <>
                    <Button variant="deleteDialog" onClick={clearSig} disabled={saving}>
                        {t('clear')}
                    </Button>
                    <Button variant="save" onClick={acceptSig} disabled={sigEmpty || saving}>
                        {t('accept')}
                    </Button>
                </>
            )}
        >
            <Box sx={{ p: 1 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {t('signatureRequiredMessage')}
                </Typography>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'inline-block',
                    }}
                >
                    <SignatureCanvas
                        penColor="#5BC6CC"
                        canvasProps={{ width: 700, height: 250, className: 'sigCanvas' }}
                        ref={sigPad}
                        onEnd={() => setSigEmpty(sigPad.current == null || sigPad.current.isEmpty())}
                    />
                </Box>
            </Box>
        </ModalDialog>
    );
};

export default SignatureGate;
